<template>
  <div ref="crudTableFitRoot" :data-testid="getTestId('user-crud-container')">
    <v-data-table
      :headers="headers"
      :items="users"
      :items-per-page="itemsPerPage"
      class="elevation-1"
      :data-testid="getTestId('user-data-table')"
      hide-default-footer
      hide-details="auto"
      :loading="apiLoading"
      :loading-text="$t('fileDetail.loadingData')"
      :custom-sort="customSort"
      @update:options="changedOptions"
    >
      <template slot="top">
        <v-toolbar flat class="solvit-crud-table-toolbar" :data-testid="getTestId('user-toolbar')">
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left>
              mdi-account
            </v-icon>{{ $t('user.management.accountCrudTitle') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('user.management.accountSearchLabel')"
            single-line
            hide-details="auto"
            :data-testid="getTestId('user-search-input')"
          />
          <v-spacer />
          <v-btn
            outlined
            class="mb-2 solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :data-testid="getTestId('user-add-button')"
            @click="openAddDialog"
          >
            <v-icon size="medium">
              mdi-plus
            </v-icon>
            <span class="ml-1">{{ $t('common.addNew') }}</span>
          </v-btn>
        </v-toolbar>
      </template>
      <template slot="item.actions" slot-scope="{ item }">
        <FixDeleteBtn
          :key="'delete-btn-' + Date.now()"
          :item="item"
          :data-testid="getTestId('user-action-buttons')"
          @editItem="editItem"
          @deleteItem="deleteItem"
        />
      </template>
      <template slot="item.passwordActions" slot-scope="{ item }">
        <v-btn
          outlined
          small
          class="solvit-crud-outline-btn"
          :data-testid="getTestId('user-password-change-button')"
          @click="openPasswordDialog(item)"
        >
          <v-icon left>
            mdi-key
          </v-icon>{{ $t('user.management.changePasswordBtn') }}
        </v-btn>
      </template>
      <template slot="item.userStatus" slot-scope="{ item }">
        <span>{{ getStatusLabel(item.userStatus) }}</span>
      </template>
      <template slot="item.userRole" slot-scope="{ item }">
        <span>{{ getRoleLabel(item.userRole) }}</span>
      </template>
      <template slot="no-data">
        <v-btn outlined class="solvit-crud-outline-btn" :data-testid="getTestId('user-refresh-button')" @click="initialize">
          {{ $t('fileDetail.refresh') }}
        </v-btn>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="10"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        :data-testid="getTestId('user-pagination')"
      />
    </div>
    <UserEditDialog
      v-if="addDialog"
      :key="addDialogKey"
      :dialog="addDialog"
      :item="defaultItem"
      :companies="companyLists"
      :data-testid="getTestId('user-add-dialog')"
      @close="closeAddDialog"
      @success="handleAddSuccess"
    />
    <ChangePasswordDialog
      v-if="passwordDialog"
      :key="passwordDialogKey"
      :dialog="passwordDialog"
      :user-seq="selectedUser?.seq"
      access-type="all"
      :data-testid="getTestId('user-password-dialog')"
      @close="closePasswordDialog"
    />
    <UserEditDialog
      v-if="editDialog"
      :key="editDialogKey"
      :dialog="editDialog"
      :companies="companyLists"
      :item="selectedItem"
      :data-testid="getTestId('user-edit-dialog')"
      @close="closeEditDialog"
      @success="handleEditSuccess"
    />
  </div>
</template>

<script>
import Api from '../../apis/Api';
import FixDeleteBtn from '../button/FixDeleteBtn.vue';
import ChangePasswordDialog from '../../components/dialog/ChangePasswordDialog.vue';
import UserEditDialog from '../../components/dialog/UserEditDialog.vue';
import {
  USER_ROLE_LABEL_I18N_KEY,
  USER_STATUS_LABEL_I18N_KEY
} from '@/utils/userI18nKeys';
import { getTestId } from '@/utils/testUtils';
import { debounce } from '@/utils/simpleDebounce';
import { transformApiResponse } from '../../utils/tableUtils';
import { transformToUserTableItems } from '../../constants/userTableItem';
import { crudTablePageSizeFitMixin } from '@/ui/mixins/crudTablePageSizeFitMixin';

export default {
  mixins: [crudTablePageSizeFitMixin],
  components: {
    FixDeleteBtn,
    ChangePasswordDialog,
    UserEditDialog
  },
  data: () => ({
    valid: true,
    sortBy: ['seq'],
    sortDesc: [true],
    itemsPerPage: 10,
    pageCount: 0,
    paging: true,
    page: 1,
    domain: 'users',
    addDialog: false,
    addDialogKey: Date.now(),
    passwordDialog: false,
    passwordDialogKey: Date.now(),
    editDialog: false,
    editDialogKey: Date.now(),
    selectedUser: null,
    search: '',
    users: [],
    companies: [],
    companyLists:[],
    editedIndex: -1,
    editedItem: {
      seq: null,
      userId: '',
      name: '',
      password: '',
      companySeq: '',
      companyName: '',
      userImagePath: null,
      userRole: 'USER',
      userStatus: 'ACTIVE'
    },
    defaultItem: {
      userId: '',
      name: '',
      password: '',
      companySeq: '',
      companyName: '',
      userImagePath: null,
      userRole: 'USER',
      userStatus: 'ACTIVE'
    },
    apiLoading: true,
    selectedItem: null
  }),
  computed: {
    headers() {
      void this.$i18n.locale;
      return [
        { text: this.$t('user.management.fields.userId'), value: 'userId', sortable: true },
        { text: this.$t('user.management.fields.name'), value: 'name', sortable: true },
        { text: this.$t('user.management.fields.company'), value: 'companyName', sortable: true },
        { text: this.$t('user.management.fields.role'), value: 'userRole', sortable: true },
        { text: this.$t('user.management.colStatus'), value: 'userStatus', sortable: true },
        {
          text: this.$t('user.management.changePasswordBtn'),
          value: 'passwordActions',
          sortable: false,
          align: 'center',
          width: '120px'
        },
        {
          text: this.$t('user.management.userRowActionsCol'),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '120px'
        }
      ];
    },
    formTitle() {
      return this.editedIndex === -1 ? this.$t('common.addNew') : this.$t('common.edit');
    }
  },
  watch: {
    search(val) {
      if (this.paging) {  // paging이 true일 때만 API 호출
        // 검색어 변경 시에만 debounce 적용
        this.debouncedSearch(val, 1, this.sortBy, this.sortDesc);
      }
    },
    page(val) {
      if (this.isCrudTableFitProgrammaticUpdate()) {
        return;
      }
      if (this.paging) {  // paging이 true일 때만 API 호출
        // 페이지 변경은 즉시 실행
        this.searchUserData(this.search, val, this.sortBy, this.sortDesc);
      }
    }
  },
  created() {
    this.paging = false;
    this.initialize();

    // 간단한 debounce 함수 생성
    this.debouncedSearch = debounce(this.searchUserData, 200);
  },
  methods: {
    getTestId(testId) {
      return getTestId(testId);
    },
    /**
     * 기존의 sort기능을 사용하지 않도록 처리
     */
    customSort(items, _index, _isDesc) {
      return items;
    },
    changeSortByArrayToApiParam(sortBy) {
      if (sortBy && sortBy?.length > 0) {
        if (sortBy[0] === 'userId') return ['userId'];
        else if (sortBy[0] === 'name') return ['name'];
        else if (sortBy[0] === 'companyName') return ['company.companyName'];
        else if (sortBy[0] === 'userRole') return ['userRole'];
        else if (sortBy[0] === 'userStatus') return ['userStatus'];
        else return ['seq'];
      }
      return ['seq'];
    },
    changedOptions(val) {
      /**
       * groupBy : Array, groupDesc : Array, itemsPerPage : Number, multiSort : Boolean, mustSort : Boolean, page : Number, sortBy : Array, sortDesc : Array
       */

      const tempPage = this.page;
      if (val?.sortBy?.length > 0) {
        this.sortBy = val.sortBy;
      } else {
        this.sortBy = ['seq'];
      }
      if (val?.sortDesc?.length > 0) {
        this.sortDesc = val.sortDesc;
      }
      this.paging = true;  // API 호출 허용
      // 정렬 변경은 즉시 실행
      this.searchUserData(this.search, tempPage, this.sortBy, this.sortDesc);
    },
    /**
     * user 데이터 조회
     * @param keyword
     * @param sortBy (seq, createTime, solarExamineReport, processStatus )
     * @param sortDesc (sortDesc: true는 내림차순, false는 오름차순)
     * @param page
     */
    searchUserData(keyword, page, sortBy, sortDesc) {
      if (!this.paging) return;  // paging이 false면 API 호출하지 않음

      const request = this.beginCrudTableRequest();
      this.apiLoading = true;
      if (sortDesc === undefined || sortDesc == null) {
        sortDesc = [false];
      }
      const tempSortBy = this.changeSortByArrayToApiParam(sortBy);

      // Promise.all을 사용하여 병렬로 API 호출
      Promise.all([
        // 사용자 데이터 조회 - 가독성 개선: API 호출 바로 위에서 params 구성
        Api.getApiResource(`/${this.domain}`, {
          size: this.itemsPerPage,
          page: !isNaN(page) ? page - 1 : 0,
          sort: `${tempSortBy.length > 0 ? tempSortBy?.[0] : 'seq'},${sortDesc?.[0] ? 'ASC' : 'DESC'}`,
          ...(keyword && { keyword })
        }, { signal: request.signal }),
        // 회사 목록 조회 (한 번만 호출)
        Api.getApiResource('/companies', { size: 200 }, { signal: request.signal })
      ])
        .then(([userRes, companyRes]) => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;

          // 사용자 데이터 처리
          const transformedData = transformApiResponse(userRes.data, (items) => {
            return transformToUserTableItems(items);
          });
          
          this.users = transformedData.items;
          this.pageCount = transformedData.pageCount;
          this.page = transformedData.page;

          // 회사 데이터 처리
          this.companyLists = Array.isArray(companyRes?.data?.content?.content)
            ? companyRes.data.content.content
            : [];
          this.companies = this.companyLists; // 동일한 데이터 사용
        })
        .catch((error) => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          console.error('사용자 데이터 조회 중 오류 발생:', error);
        })
        .finally(() => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          this.apiLoading = false;
          this.paging = true;  // API 호출 허용
          this.finishCrudTableRequest(request.token);
        });
    },
    initialize() {
      this.searchUserData();
    },
    openPasswordDialog(item) {
      this.selectedUser = Object.assign({}, item);
      this.passwordDialog = true;
      this.passwordDialogKey = Date.now();
    },
    closePasswordDialog() {
      this.passwordDialog = false;
      this.selectedUser = null;
    },
    editItem(item) {
      this.selectedItem = Object.assign({}, item);
      this.editDialog = true;
      this.editDialogKey = Date.now();
    },
    deleteItem(item) {
      const index = this.users.indexOf(item);
      confirm(this.$t('common.confirmDeleteSelected')) &&
        Api.delete(`/${this.domain}/${item.seq}`).then(() => {
          this.users.splice(index, 1);
          this.initialize();
        });
    },
    closeEditDialog() {
      this.editDialog = false;
      this.selectedItem = null;
    },
    handleEditSuccess() {
      this.closeEditDialog();
      this.paging = true;  // success 이벤트 후 API 호출 허용
      this.searchUserData();
    },
    openAddDialog() {
      this.addDialog = true;
      this.addDialogKey = Date.now();
    },
    closeAddDialog() {
      this.addDialog = false;
    },
    handleAddSuccess() {
      this.closeAddDialog();
      this.initialize();
    },
    fetchUsers() {
      this.initialize();
    },
    getStatusLabel(status) {
      const key = USER_STATUS_LABEL_I18N_KEY[status];
      return key ? this.$t(key) : status;
    },
    getRoleLabel(role) {
      const key = USER_ROLE_LABEL_I18N_KEY[role];
      return key ? this.$t(key) : role;
    }
  }
};
</script>

<style scoped>
.v-dialog__content {
  max-height: 100%;
}
.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}
.custom-padding {
  padding: 8px 12px !important;
}

.required::after {
  content: "*";
  color: red;
  margin-left: 4px;
}
</style>
